import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronDown, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const ADD_CUSTOMER_SUPERVISOR = "__add_customer_supervisor__";

export const CustomerSupervisorSelect = ({ onChange, testId, value }) => {
  const [names, setNames] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const loadNames = async () => {
    try {
      const response = await axios.get(`${API}/customer-supervisors`);
      setNames(response.data.customer_supervisors || []);
    } catch {
      setNames([]);
    }
  };

  useEffect(() => {
    loadNames();
  }, []);

  const options = useMemo(() => {
    const available = new Set(names);
    if (value) {
      available.add(value);
    }
    return [...available].sort((first, second) => first.localeCompare(second));
  }, [names, value]);

  const selectName = (event) => {
    const nextValue = event.target.value;
    if (nextValue === ADD_CUSTOMER_SUPERVISOR) {
      setAdding(true);
      return;
    }
    onChange(nextValue);
  };

  const addName = async () => {
    const name = newName.trim();
    if (!name) {
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(`${API}/customer-supervisors`, { name });
      onChange(response.data.name);
      setNewName("");
      setAdding(false);
      await loadNames();
      toast.success("Customer Supervisor saved for future projects.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Customer Supervisor could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="project-assignment-control">
      <select
        data-testid={testId}
        onChange={selectName}
        value={value || ""}
      >
        <option value={ADD_CUSTOMER_SUPERVISOR}>+ Add Customer Supervisor…</option>
        <option value="">Unassigned</option>
        {options.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
      {adding && (
        <div className="project-assignment-add-row" data-testid={`${testId}-add-row`}>
          <input
            data-testid={`${testId}-add-name-input`}
            onChange={(event) => setNewName(event.target.value)}
            placeholder="Customer Supervisor name"
            value={newName}
          />
          <button
            data-testid={`${testId}-add-name-button`}
            disabled={saving || !newName.trim()}
            onClick={addName}
            type="button"
          >
            <Plus size={14} />
            {saving ? "Saving…" : "Add"}
          </button>
        </div>
      )}
    </div>
  );
};

export const SupervisorSelect = ({ onChange, testId, users, value }) => {
  const supervisors = users.filter((user) => user.role === "supervisor");
  const options = value && !supervisors.some((user) => user.name === value)
    ? [{ id: "current", name: value }, ...supervisors]
    : supervisors;

  return (
    <select data-testid={testId} onChange={(event) => onChange(event.target.value)} value={value || ""}>
      <option value="">Unassigned</option>
      {options.map((user) => (
        <option key={user.id} value={user.name}>
          {user.name}
        </option>
      ))}
    </select>
  );
};

export const ProjectManagerDropdown = ({
  label = "Project Manager",
  onChange,
  role = "project_manager",
  testId,
  users,
  value,
}) => {
  const [open, setOpen] = useState(false);
  const managers = users.filter((user) => user.role === role);
  const selectedIds = value || [];
  const selectedNames = managers
    .filter((user) => selectedIds.includes(user.id))
    .map((user) => user.name);

  const toggleManager = (managerId) => {
    onChange(
      selectedIds.includes(managerId)
        ? selectedIds.filter((id) => id !== managerId)
        : [...selectedIds, managerId],
    );
  };

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <button
          className="project-manager-dropdown-trigger"
          data-testid={`${testId}-trigger`}
          type="button"
        >
          <span>{selectedNames.join(", ") || `Select ${label}`}</span>
          <ChevronDown size={15} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="project-manager-dropdown-menu">
        {managers.length ? managers.map((manager) => (
          <label key={manager.id}>
            <input
              checked={selectedIds.includes(manager.id)}
              data-testid={`${testId}-option-${manager.id}`}
              onChange={() => toggleManager(manager.id)}
              type="checkbox"
            />
            {manager.name}
          </label>
        )) : <p>No active {label}s.</p>}
      </PopoverContent>
    </Popover>
  );
};

export const ProjectSupervisorDropdown = (props) => (
  <ProjectManagerDropdown {...props} label="Company Supervisor" role="supervisor" />
);